/**
 * Bulk "Move to…" — the pure reducer.
 *
 * The sidebar already has a reducer of this shape for drag-and-drop
 * (`applySidebarDrop` in `Sidebar.js`), but that one is POSITION-shaped: it
 * lands the dragged row before/after/inside whatever the pointer is over. A
 * "Move to…" from the destination picker has no over-target — it has a
 * destination container (a collection root, or a folder inside one) and appends
 * to the end of it. Same primitives, different question, so it lives beside the
 * drag reducer rather than inside it.
 *
 * Everything here is pure: `(collections, ids, destination) → next collections`.
 */

import {
  getFolders,
  getOrderedChildren,
  withCollectionFolders,
  collectFolderSubtreeIds,
  isDescendantFolder,
  normalizeParentId,
  stripFolderIdForPayload,
} from './collectionFolders';
import { parseRowId, normalizeSelection } from './treeSelection';

/** Highest sort_order currently sitting directly inside `parentFolderId` (−1 when empty). */
function maxChildOrder(collection, parentFolderId) {
  return getOrderedChildren(collection, parentFolderId).reduce(
    (max, entry) => Math.max(max, typeof entry.sort_order === 'number' ? entry.sort_order : 0),
    -1
  );
}

/** Lift one request or one folder SUBTREE out of a collection. */
function extract(collection, parsed) {
  if (parsed.kind === 'request') {
    const request = (collection.requests || []).find((r) => String(r.id) === String(parsed.key));
    if (!request) return null;
    return {
      nextSource: {
        ...collection,
        requests: (collection.requests || []).filter((r) => String(r.id) !== String(parsed.key)),
      },
      folders: [],
      requests: [{ ...request }],
    };
  }

  const folders = getFolders(collection);
  if (!folders.some((f) => String(f.id) === String(parsed.key))) return null;
  const subtreeIds = collectFolderSubtreeIds(folders, parsed.key);
  const movingFolders = folders.filter((f) => subtreeIds.has(String(f.id))).map((f) => ({ ...f }));
  const movingRequests = (collection.requests || [])
    .filter((r) => r.folder_id != null && subtreeIds.has(String(r.folder_id)))
    .map((r) => ({ ...r }));

  return {
    nextSource: {
      ...withCollectionFolders(
        collection,
        folders.filter((f) => !subtreeIds.has(String(f.id)))
      ),
      requests: (collection.requests || []).filter(
        (r) => r.folder_id == null || !subtreeIds.has(String(r.folder_id))
      ),
    },
    folders: movingFolders,
    requests: movingRequests,
  };
}

/** Drop a lifted subtree into `destFolderId` of `collection`, appended at the end. */
function append(collection, parsed, lifted, destFolderId, sortOrder) {
  const pid = normalizeParentId(destFolderId);

  if (parsed.kind === 'request') {
    const moving = lifted.requests[0];
    const { settings, folder_id } = stripFolderIdForPayload({ ...moving, folder_id: pid });
    return {
      ...collection,
      requests: [
        ...(collection.requests || []),
        { ...moving, folder_id, settings, sort_order: sortOrder },
      ],
    };
  }

  // Only the subtree ROOT is re-parented; its descendants keep pointing at their
  // own parents, and the requests inside keep their folder_id.
  const rootId = String(parsed.key);
  const nextFolders = [
    ...getFolders(collection),
    ...lifted.folders.map((f) =>
      String(f.id) === rootId ? { ...f, parent_id: pid, sort_order: sortOrder } : { ...f }
    ),
  ];
  return {
    ...withCollectionFolders(collection, nextFolders),
    requests: [...(collection.requests || []), ...lifted.requests.map((r) => ({ ...r }))],
  };
}

/**
 * Move every marked row into `{ collectionId, folderId }`.
 *
 * The selection is normalised first, so a folder and a request inside it move
 * ONCE, as one subtree — never twice, and never with the child left orphaned in
 * a location its parent has already left.
 *
 * Returns the next collections array, or `null` when nothing legally moved (an
 * unknown or read-only destination, a folder dropped into its own subtree, an
 * empty selection). `null` means "no-op", exactly like `applySidebarDrop`.
 */
export function moveEntriesToDestination(collections, ids, { collectionId, folderId = null } = {}) {
  const cols = Array.isArray(collections) ? collections : [];
  const destIndex = cols.findIndex((c) => String(c.id) === String(collectionId));
  if (destIndex === -1) return null;
  if (cols[destIndex].can_edit === false) return null;

  const targets = normalizeSelection(ids, cols)
    .map(parseRowId)
    .filter((p) => p && p.kind !== 'collection');
  if (targets.length === 0) return null;

  let next = [...cols];
  let moved = false;
  let order = maxChildOrder(next[destIndex], folderId) + 1;

  for (const parsed of targets) {
    const sourceIndex = next.findIndex((c) =>
      parsed.kind === 'request'
        ? (c.requests || []).some((r) => String(r.id) === String(parsed.key))
        : (c.settings?.folders || []).some((f) => String(f.id) === String(parsed.key))
    );
    if (sourceIndex === -1) continue;
    if (next[sourceIndex].can_edit === false) continue;

    if (parsed.kind === 'folder') {
      // A folder may never land inside itself or its own descendants.
      if (folderId != null && String(folderId) === String(parsed.key)) continue;
      if (
        sourceIndex === destIndex &&
        folderId != null &&
        isDescendantFolder(getFolders(next[destIndex]), folderId, parsed.key)
      ) {
        continue;
      }
    }

    const lifted = extract(next[sourceIndex], parsed);
    if (!lifted) continue;
    // `order` is captured per iteration, not read from the loop variable — the
    // map callbacks below would otherwise close over a value that keeps moving.
    const sortOrder = order;
    next = next.map((c, i) => (i === sourceIndex ? lifted.nextSource : c));
    next = next.map((c, i) =>
      i === destIndex ? append(c, parsed, lifted, folderId, sortOrder) : c
    );
    order += 1;
    moved = true;
  }

  return moved ? next : null;
}

/**
 * Resolve a drop TARGET into the destination container a bulk move needs.
 *
 * `applySidebarDrop` can honour a precise before/after position because it moves
 * one row. A multi-row drag cannot: "put these 20 between A and B" has no single
 * answer that survives the items having come from different parents. So a bulk
 * drag reparents into the container under the cursor — exactly what "Move to"
 * does — and the fine ordering is whatever `moveEntriesToDestination` appends.
 * Dropping *between* two rows therefore targets the container they live in, not
 * the gap.
 *
 * @returns {{collectionId: string, folderId: string|null}|null}
 */
export function destinationForDropTarget(collections, overId, position) {
  const cols = Array.isArray(collections) ? collections : [];
  const parsed = parseRowId(overId);
  if (!parsed) return null;

  if (parsed.kind === 'collection') {
    const col = cols.find((c) => String(c.id) === String(parsed.key));
    return col ? { collectionId: col.id, folderId: null } : null;
  }

  if (parsed.kind === 'folder') {
    const col = cols.find((c) =>
      (c.settings?.folders || []).some((f) => String(f.id) === String(parsed.key))
    );
    if (!col) return null;
    // Only a drop ON the folder means "into it". Above or below it means the
    // folder's own parent — otherwise nudging a row past a folder would swallow
    // the whole selection into it.
    if (position === 'inside') return { collectionId: col.id, folderId: String(parsed.key) };
    const folder = (col.settings?.folders || []).find(
      (f) => String(f.id) === String(parsed.key)
    );
    return { collectionId: col.id, folderId: normalizeParentId(folder?.parent_id) };
  }

  if (parsed.kind === 'request') {
    const col = cols.find((c) =>
      (c.requests || []).some((r) => String(r.id) === String(parsed.key))
    );
    if (!col) return null;
    const req = (col.requests || []).find((r) => String(r.id) === String(parsed.key));
    return { collectionId: col.id, folderId: normalizeParentId(req?.folder_id) };
  }

  return null;
}
